import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { Phone, ShoppingCart, MapPin, Home, Map, Crosshair, Monitor, Music, Gem, Wrench, Target, FileText, HelpCircle } from 'lucide-react';
import CITIES, { getCityUrl, STATE_FULL } from '@/data/cities';

const LOGO_URL = "/images/cave-city-pawn-logo.png";
const CAVEMAN_LAPTOP = "/images/caveman-laptop.png";

const stateNames = { FL: 'Florida', AL: 'Alabama', GA: 'Georgia' };

const mainSections = [
  { label: 'Home', href: '/', icon: Home, desc: 'Main page with all shop information' },
  { label: 'What We Carry', href: '/#categories', icon: Crosshair, desc: 'Firearms, Electronics, Instruments, Jewelry, Tools & Archery' },
  { label: 'Sell or Pawn', href: '/#pawn-process', icon: FileText, desc: 'Learn how pawning and selling works' },
  { label: 'Archery Equipment', href: '/#gun-range', icon: Target, desc: 'Our large selection of bows, crossbows & accessories' },
  { label: 'About Us', href: '/#about', icon: HelpCircle, desc: '35+ years of experience, knowledgeable team' },
  { label: 'Contact & Map', href: '/#contact', icon: MapPin, desc: 'Address, phone, hours, and Google Map' },
  { label: 'Service Areas', href: '/locations', icon: Map, desc: 'All 45 cities and towns we serve' },
];

const productCategories = [
  { name: 'Firearms & Guns', icon: Crosshair, keywords: 'Handguns, Rifles, Shotguns, Ammunition' },
  { name: 'Electronics', icon: Monitor, keywords: 'Laptops, Tablets, Smartphones, Gaming Consoles, TVs' },
  { name: 'Musical Instruments', icon: Music, keywords: 'Guitars, Amps, Drums, Keyboards' },
  { name: 'Jewelry & Gold', icon: Gem, keywords: 'Gold, Silver, Diamonds, Watches' },
  { name: 'Power Tools', icon: Wrench, keywords: 'DeWalt, Milwaukee, Makita, Drills, Saws' },
  { name: 'Archery Equipment', icon: Target, keywords: 'Compound Bows, Recurve, Crossbows, Arrows' },
];

export default function SitemapPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Sitemap | Cave City Pawn Shop & Trading Company';
    let el = document.querySelector('meta[name="description"]');
    if (!el) { el = document.createElement('meta'); el.setAttribute('name', 'description'); document.head.appendChild(el); }
    el.setAttribute('content', 'Complete sitemap for Cave City Pawn Shop. Find all pages including service areas across Florida, Alabama, and Georgia.');
    return () => { document.title = 'Cave City Pawn Shop | Buy, Sell & Pawn Guns, Electronics, Jewelry & More'; };
  }, []);

  const grouped = {};
  CITIES.forEach(c => {
    const state = stateNames[c.state] || c.state;
    if (!grouped[state]) grouped[state] = [];
    grouped[state].push(c);
  });
  Object.keys(grouped).forEach(s => grouped[s].sort((a, b) => a.name.localeCompare(b.name)));

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Nav */}
      <nav className="bg-dirt-brown/95 backdrop-blur-sm shadow-deep py-2 sticky top-0 z-50">
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-900 via-amber-700 to-amber-900 opacity-60" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 shrink-0">
            <img src={LOGO_URL} alt="Cave City Pawn Shop" className="h-10" width="auto" height={40} />
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <a href="https://alabamapawnandstorage.com/pawn-shop" target="_blank" rel="noopener noreferrer"
              className="cave-btn px-4 py-2 bg-magma-red text-white font-heading text-xs sm:text-sm uppercase tracking-wider rounded-rock shadow-deep flex items-center gap-1.5">
              <ShoppingCart size={14} /> Shop Online
            </a>
            <a href="tel:850-615-6105"
              className="cave-btn px-3 py-2 border-2 border-accent text-accent font-heading text-xs sm:text-sm uppercase rounded-rock flex items-center gap-1.5 hover:bg-accent hover:text-dirt-brown transition-colors">
              <Phone size={14} /> <span className="hidden sm:inline">Call Now</span><span className="sm:hidden">Call</span>
            </a>
          </div>
        </div>
      </nav>

      <main>
        {/* Header */}
        <section className="relative py-12 md:py-16 cave-gradient noise-overlay overflow-hidden">
          <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
              <div>
                <nav aria-label="Breadcrumb" className="mb-4">
                  <ol className="flex items-center gap-2 text-sm font-body text-stone-light/70">
                    <li><Link to="/" className="hover:text-accent transition-colors">Home</Link></li>
                    <li className="text-stone-light/50">/</li>
                    <li className="text-accent">Sitemap</li>
                  </ol>
                </nav>
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-heading text-bone-white uppercase mb-3" data-testid="sitemap-h1">
                  Site <span className="text-accent">Map</span>
                </h1>
                <p className="text-base text-stone-light font-body max-w-xl">
                  Every page on Cave City Pawn Shop, organized for easy navigation. Find what you need or explore our 45+ service areas.
                </p>
              </div>
              <img src={CAVEMAN_LAPTOP} alt="Caveman browsing sitemap" className="hidden md:block w-36 lg:w-44 caveman-float drop-shadow-xl" loading="lazy" />
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0">
            <svg viewBox="0 0 1440 50" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
              <path d="M0 50L80 42C160 34 320 18 480 12C640 6 800 10 960 16C1120 22 1280 30 1360 34L1440 38V50H0Z" fill="#F4EBD9"/>
            </svg>
          </div>
        </section>

        {/* Main Pages */}
        <section className="py-12 md:py-16" data-testid="sitemap-main">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-xl md:text-2xl font-heading text-dirt-brown uppercase mb-6 chisel-heading">
              Main <span className="text-magma-red">Pages</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {mainSections.map(sec => {
                const Icon = sec.icon;
                const isExternal = sec.href.startsWith('/#');
                const Comp = isExternal ? 'a' : Link;
                const props = isExternal ? { href: sec.href } : { to: sec.href };
                return (
                  <Comp key={sec.label} {...props} data-testid={`sitemap-link-${sec.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className="stone-card bg-card border-2 border-dirt-brown/10 rounded-rock-lg p-5 group hover:border-magma-red/30 transition-all flex gap-4 items-start">
                    <div className="category-icon w-10 h-10 bg-magma-red/10 rounded-full flex items-center justify-center shrink-0 group-hover:bg-magma-red transition-colors duration-300">
                      <Icon size={18} className="text-magma-red group-hover:text-white transition-colors duration-300" />
                    </div>
                    <div>
                      <h3 className="font-heading text-sm text-dirt-brown uppercase group-hover:text-magma-red transition-colors">{sec.label}</h3>
                      <p className="text-xs text-muted-foreground font-body mt-1">{sec.desc}</p>
                    </div>
                  </Comp>
                );
              })}
            </div>
          </div>
        </section>

        {/* Product Categories */}
        <section className="py-10 md:py-14 bg-muted/50" data-testid="sitemap-products">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-xl md:text-2xl font-heading text-dirt-brown uppercase mb-6 chisel-heading">
              What We <span className="text-magma-red">Carry</span>
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {productCategories.map(cat => {
                const Icon = cat.icon;
                return (
                  <a key={cat.name} href="/#categories"
                    className="stone-card bg-card border-2 border-dirt-brown/10 rounded-rock-lg p-4 text-center group hover:border-magma-red/30 transition-all">
                    <Icon size={24} className="text-magma-red mx-auto mb-2" />
                    <h3 className="font-heading text-xs text-dirt-brown uppercase leading-tight">{cat.name}</h3>
                    <p className="text-[10px] text-muted-foreground font-body mt-1 leading-tight">{cat.keywords}</p>
                  </a>
                );
              })}
            </div>
          </div>
        </section>

        {/* Service Areas by State */}
        <section className="py-12 md:py-16" data-testid="sitemap-cities">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl md:text-2xl font-heading text-dirt-brown uppercase chisel-heading">
                Service <span className="text-magma-red">Areas</span>
              </h2>
              <span className="text-sm font-body text-muted-foreground bg-muted px-3 py-1 rounded-full">{CITIES.length} cities</span>
            </div>

            {Object.entries(grouped).map(([state, cities]) => (
              <div key={state} className="mb-10 last:mb-0">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 bg-dirt-brown rounded-full flex items-center justify-center">
                    <MapPin size={14} className="text-accent" />
                  </div>
                  <h3 className="font-heading text-lg text-dirt-brown uppercase">{state}</h3>
                  <span className="text-xs font-body text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{cities.length} cities</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  {cities.map(c => (
                    <Link key={c.slug} to={getCityUrl(c)} data-testid={`sitemap-city-${c.slug}`}
                      className="flex items-center gap-2 px-3 py-2.5 bg-card border border-dirt-brown/8 rounded-lg hover:border-magma-red/30 hover:bg-magma-red/5 transition-all group">
                      <span className="w-1.5 h-1.5 bg-magma-red rounded-full shrink-0 group-hover:scale-150 transition-transform" />
                      <div className="min-w-0">
                        <p className="font-body font-semibold text-sm text-foreground truncate group-hover:text-magma-red transition-colors">{c.name}</p>
                        <p className="text-[10px] text-muted-foreground font-body">{c.distance === 0 ? 'Home base' : `${c.distance} mi ${c.direction}`}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* XML Sitemap link + CTA */}
        <section className="py-10 bg-dirt-brown">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-sm text-stone-light font-body mb-4">
              For search engines: <a href="/sitemap.xml" className="text-accent underline underline-offset-4 hover:text-white transition-colors font-semibold">sitemap.xml</a>
            </p>
            <a href="tel:850-615-6105" className="cave-btn inline-flex items-center gap-2 px-7 py-3.5 bg-accent text-dirt-brown font-heading text-base uppercase rounded-rock shadow-deep hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all">
              <Phone size={20} /> Call 850-615-6105
            </a>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="relative bg-foreground text-bone-white py-8">
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-dirt-brown via-stone-dark to-dirt-brown" />
        <div className="max-w-5xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-stone-dark font-body">&copy; {new Date().getFullYear()} Cave City Pawn Shop &amp; Trading Company. All rights reserved.</p>
          <div className="flex gap-4">
            <Link to="/" className="text-sm text-stone-light font-body hover:text-accent">Home</Link>
            <Link to="/locations" className="text-sm text-stone-light font-body hover:text-accent">Locations</Link>
            <a href="https://alabamapawnandstorage.com/pawn-shop" target="_blank" rel="noopener noreferrer" className="text-sm text-stone-light font-body hover:text-accent">Shop Online</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
